<template>
  <v-card v-if="user" class="settings-card">
    <div class="user-header">
      <div class="profile-pic">
        <img
          :src="profilePicture"
          :alt="name"
          @error="getAvatarUrl($event, user.name)"
        >
        <div class="overlay">
          <v-btn
            icon
            text
            @click="showCropper = true"
          >
            <v-icon>camera_alt</v-icon>
          </v-btn>
        </div>
      </div>
      <div class="profile-name-input-wrapper">
        <v-text-field
          v-model="name"
          ref="nameFiledRef"
          label="Name"
          required
          :readonly="!editingName"
          background-color="transparent"
          :rules="nameRules"
          validate-on-blur
          solo
          class="name-field mt-5 mr-0"
          @blur="handleNameSave"
          v-on:keyup.enter="handleNameSave"
        >
        </v-text-field>
        <v-btn
          text
          icon
          @mousedown.prevent="handleEditName"
        >
          <v-icon :color="colorPalette.darkGrayColor">
            <template v-if="!editingName">edit</template>
            <template v-else>done</template>
          </v-icon>
        </v-btn>
      </div>
    </div>
    <image-upload
      field="picture"
      @crop-success="cropSuccess"
      v-model="showCropper"
      :width="200"
      :height="200"
      langType="en"
      img-format="png">
    </image-upload>
    <div class="fields-wrapper">
      <p class="input-label text-style">Language</p>
      <v-select
        v-model="language"
        :items="languages"
        solo
        :menu-props="{ offsetY: true }"
        class="select-field black--text"
        hide-details
      >
      </v-select>
      <p class="input-label text-style">Data & number format</p>
      <v-select
        v-model="dataNumberFormat"
        :items="dataNumberFormats"
        solo
        :menu-props="{ offsetY: true }"
        class="select-field black--text"
        hide-details
      >
      </v-select>
      <p class="input-hint">Format: March 5, 2020, 03/05/2020 and 1,234.56</p>
    </div>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import avatarUrlMixin from '../../../mixins/avatarUrlMixin'
import imageUpload from 'vue-image-crop-upload'
export default {
  name: 'SettingsProfile',
  mixins: [avatarUrlMixin],
  components: {
    imageUpload
  },
  data () {
    return {
      name: '',
      profilePicture: '',
      editingName: false,
      nameRules: [
        v => !!v || 'Name is required'
      ],
      showCropper: false,
      language: 'en',
      languages: [
        {
          text: 'English',
          value: 'en'
        }
      ],
      dataNumberFormat: 'us',
      dataNumberFormats: [
        {
          text: 'United States',
          value: 'us'
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      user: 'user',
      colorPalette: 'colorPaletteServices/theme'
    })
  },
  mounted () {
    this.name = this.user.name
    this.profilePicture = this.user.picture
  },
  methods: {
    handleNameSave () {
      this.editingName = false
      this.$refs.nameFiledRef.blur()
      if (this.name !== this.user.name) {
        this.handleSave()
      }
    },
    handleEditName () {
      if (this.editingName) {
        this.handleNameSave()
        return
      }
      this.editingName = !this.editingName
      this.$nextTick(() => {
        if (this.editingName) {
          this.$refs.nameFiledRef.focus()
        }
      })
    },
    cropSuccess (imgDataUrl) {
      this.profilePicture = imgDataUrl
      this.handleSave()
    },
    handleSave () {
      const userData = {
        entity_id: this.user.entity_id,
        user_id: this.user.user_id,
        affiliate_id: this.user.affiliate_id,
        email: this.user.email,
        email_verified: this.user.email_verified,
        nickname: this.user.nickname,
        blocked: false,
        name: this.name,
        picture: this.profilePicture,
        eula_accepted: true,
        tour_complete: !!this.user?.tour_complete
      }
      this.$store.dispatch('updateProfile', { data: userData, shouldReload: true })
    }
  }
}
</script>

<style lang="scss">
  .settings-card {
    background: #fff !important;
    border: 1px solid #eee;
    border-color: #eee !important;
    min-height: 439px;
    border: none !important;
    padding: 30px 0px;
    .select-field {
      .v-input__slot {
        padding: 0px !important;
      }
    }
    .user-header {
      display: flex;
      align-items: center;
      width: 536px;
      max-width: 100%;
      .profile-pic {
        position: relative;
        border-radius: 50%;
        width: 86px;
        height: 86px;
        overflow: hidden;
        .overlay {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          background: -webkit-linear-gradient(165.27deg, $primary-color 2.61%, $secondary-color 88.02%);
          top: 0;
          z-index: 1;
          opacity: 0;
          transition: 0.3s;
        }
        &:hover {
          .overlay {
            opacity: 1;
          }
        }
        img {
          width: 86px;
          height: 86px;
        }
      }
      .profile-name-input-wrapper {
        display: flex;
        align-items: center;
        flex: 1;
        .v-input {
          width: 200px;
          font-size: 20px;
          color: #fff;
          font-weight: bold;
          outline: none;
          border-radius: 5px;
          padding: 0 10px;
          margin-right: 10px;
          transition: 0.3s;
          &__slot {
            box-shadow: none !important;
          }
          input {
            width: 100px;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            max-height: 55px;
          }
        }
      }
    }
    .fields-wrapper {
      margin-top: 35px;
    }
    .input-label {
      color: $setting-dark-color;
      font-size: 14px;
      margin: 25px 0 8px 0;
    }
    .input-hint {
      font-size: 12px;
      line-height: 14px;
      color: $setting-dark-color;
      opacity: 0.5;
      margin-top: 8px;
    }
    .v-select {
      width: 536px;
      max-width: 100%;
    }
    .name-field {
      input {
        color: $setting-black-color;
      }
    }
  }
</style>
